import { useEffect, useState } from 'react';

export interface InView<T extends Element> {
  /** Attach to the element to watch — as a callback ref, so it may change between renders. */
  ref: (node: T | null) => void;
  /** Whether any of the element is on screen now. */
  inView: boolean;
}

/** Without IntersectionObserver the browser can't say, so everything counts as on screen and nothing waits forever. */
function canObserveVisibility(): boolean {
  return typeof IntersectionObserver !== 'undefined';
}

function startsInView(): boolean {
  return !canObserveVisibility();
}

/**
 * Whether an element is on screen, for work worth doing only once it can be
 * seen — a thumbnail's download. False until the browser first reports the
 * element, then it follows it in and out of view.
 */
export function useInView<T extends Element>(): InView<T> {
  const [node, setNode] = useState<T | null>(null);
  const [inView, setInView] = useState(startsInView);

  useEffect(() => {
    if (!node || !canObserveVisibility()) return undefined;
    const observer = new IntersectionObserver(function reportVisibility(entries) {
      const latest = entries.at(-1);
      if (latest) setInView(latest.isIntersecting);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return { ref: setNode, inView };
}
